import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  CssBaseline,
  Grid,
  Typography,
  Chip,
  Box,
  Divider,
  LinearProgress,
  Tooltip,
  Badge,
  IconButton,
  Popover
} from "@mui/material";
import { withStyles } from "@mui/styles";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import Picker from "emoji-picker-react";
import Footer from "../../components/footer";
import HeaderMenu from "../../components/header-menu";
import CustomizedSnackbars from "../../components/material-snackbars";
import Api from "../../services/api";
import {
  getProviderPredefinedMessageTemplates,
  updateProviderPredefinedMessageTemplates
} from "../../services/2talk/provider-predefined-message-templates.service";
import { Overlay } from "../../styles/global";
import { Styles } from "../../styles/material-styles";

// WhatsApp rich text editor.
const PLACEHOLDER_AT_START_RE = /^(\{\{[^}]+\}\}|\{min\}|\{max\})/;
const FORMAT_RULES = [
  { key: "code", prefix: "```", suffix: "```", className: "wa-format-code" },
  { key: "bold", prefix: "*", suffix: "*", className: "wa-format-bold" },
  { key: "italic", prefix: "_", suffix: "_", className: "wa-format-italic" },
  { key: "strike", prefix: "~", suffix: "~", className: "wa-format-strike" }
];
const HISTORY_LIMIT = 80;
const GROUP_LABELS_PT_BR = {
  optin: "Opt-in",
  satisfactionsurveylink: "Pesquisa com link",
  chatbotevaluation: "Avaliação do chatbot",
  satisfactionsurveysequential: "Pesquisa sequencial",
  acknowledgement: "Agradecimento",
  agradecimento: "Agradecimento"
};
const PLACEHOLDER_DESCRIPTIONS_BY_MESSAGE_ID = {
  2: {
    "{{1}}": "Id do chamado"
  },
  3: {
    "{{1}}": "Nome do solicitante do chamado",
    "{{2}}": "Id do chamado",
    "{{3}}": "Título do chamado",
    "{{4}}": "Data de finalização do chamado",
    "{{5}}": "Nome do técnico do chamado",
    "{{6}}": "Descrição do chamado",
    "{{7}}": "Acompanhamento de solução do chamado"
  },
  4: {
    "{{1}}": "Dado personalizado dinâmico"
  },
  5: {
    "{{1}}": "Dado personalizado dinâmico"
  },
  6: {
    "{{1}}": "Dado personalizado dinâmico"
  },
  7: {
    "{{1}}": "Dado personalizado dinâmico"
  },
  8: {
    "{{1}}": "Dado personalizado dinâmico"
  },
  9: {
    "{{1}}": "Dado personalizado dinâmico"
  },
  10: {
    "{{1}}": "Dado personalizado dinâmico"
  },
  11: {
    "{{1}}": "Id do chamado"
  },
  18: {
    "{min}": "Menor valor permitido",
    "{max}": "Maior valor permitido"
  }
};

function normalizeGroupLabelKey(value) {
  return String(value || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

function getProviderGroupLabel(groupKey, groupLabel) {
  const translated =
    GROUP_LABELS_PT_BR[normalizeGroupLabelKey(groupKey)] ||
    GROUP_LABELS_PT_BR[normalizeGroupLabelKey(groupLabel)];

  return translated || groupLabel || groupKey || "Grupo";
}

function getPlaceholderDescriptions(messageId) {
  return PLACEHOLDER_DESCRIPTIONS_BY_MESSAGE_ID[Number(messageId)] || {};
}

function getPlaceholderDescription(placeholderDescriptions, placeholder) {
  return placeholderDescriptions && placeholderDescriptions[placeholder]
    ? placeholderDescriptions[placeholder]
    : "";
}

function escapeHTML(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttribute(value) {
  return escapeHTML(value)
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function getPlaceholderAt(text, index) {
  const match = text.slice(index).match(PLACEHOLDER_AT_START_RE);
  return match ? match[0] : null;
}

function isSpace(value) {
  return /\s/.test(value || "");
}

function hasFormatBoundaryBefore(text, index) {
  if (index === 0) return true;
  return /[\s([{]/.test(text[index - 1]);
}

function hasFormatBoundaryAfter(text, index) {
  if (index >= text.length) return true;
  return /[\s.,!?;:)\]}]/.test(text[index]);
}

function findFormatAt(text, index, allowFormatting) {
  if (!allowFormatting) return null;

  for (const rule of FORMAT_RULES) {
    if (!text.startsWith(rule.prefix, index)) {
      continue;
    }

    const contentStart = index + rule.prefix.length;
    const closeIndex = text.indexOf(rule.suffix, contentStart);
    if (closeIndex < contentStart) {
      continue;
    }

    const content = text.slice(contentStart, closeIndex);
    const endIndex = closeIndex + rule.suffix.length;
    if (!content) {
      continue;
    }

    if (rule.key !== "code") {
      if (isSpace(text[contentStart]) || isSpace(text[closeIndex - 1])) {
        continue;
      }

      if (!hasFormatBoundaryBefore(text, index) || !hasFormatBoundaryAfter(text, endIndex)) {
        continue;
      }
    }

    return { rule, content, endIndex };
  }

  return null;
}

function buildFormattedHTML(text, allowFormatting = true, placeholderDescriptions = {}) {
  if (!text) return "";

  let html = "";
  let index = 0;

  while (index < text.length) {
    const placeholder = getPlaceholderAt(text, index);
    if (placeholder) {
      const escapedPlaceholder = escapeAttribute(placeholder);
      const description = getPlaceholderDescription(placeholderDescriptions, placeholder);
      const tooltip = description || placeholder;
      const ariaLabel = description ? `${placeholder}: ${description}` : placeholder;
      html += `<span class="ph-chip" contenteditable="false" data-ph="${escapedPlaceholder}" title="${escapeAttribute(tooltip)}" aria-label="${escapeAttribute(ariaLabel)}">${escapeHTML(placeholder)}</span>`;
      index += placeholder.length;
      continue;
    }

    const formatMatch = findFormatAt(text, index, allowFormatting);
    if (formatMatch) {
      const { rule, content, endIndex } = formatMatch;
      const innerHTML = buildFormattedHTML(content, rule.key !== "code", placeholderDescriptions);
      html += `<span class="wa-format ${rule.className}" data-wa-prefix="${escapeAttribute(rule.prefix)}" data-wa-suffix="${escapeAttribute(rule.suffix)}">${innerHTML}</span>`;
      index = endIndex;
      continue;
    }

    html += escapeHTML(text[index]);
    index += 1;
  }

  return html;
}

function buildHTML(text, placeholderDescriptions = {}) {
  return buildFormattedHTML(text || "", true, placeholderDescriptions);
}

function getNodeRawText(node) {
  if (!node) return "";

  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || "";
  }

  if (node.nodeName === "BR") {
    return "\n";
  }

  if (node.dataset && node.dataset.ph) {
    return node.dataset.ph;
  }

  let text = "";
  node.childNodes.forEach((child) => {
    text += getNodeRawText(child);
  });

  if (node.dataset && node.dataset.waPrefix !== undefined) {
    return `${node.dataset.waPrefix}${text}${node.dataset.waSuffix || ""}`;
  }

  return text;
}

function extractText(node) {
  return getNodeRawText(node);
}

function getNodeRawLength(node) {
  return getNodeRawText(node).length;
}

function getChildIndex(node) {
  if (!node || !node.parentNode) return 0;
  return Array.prototype.indexOf.call(node.parentNode.childNodes, node);
}

function getOffsetInsideNode(node, childOffset) {
  if (node.nodeType === Node.TEXT_NODE) {
    return Math.min(childOffset, (node.textContent || "").length);
  }

  if (node.dataset && node.dataset.ph) {
    return childOffset > 0 ? node.dataset.ph.length : 0;
  }

  let offset = 0;
  if (node.dataset && node.dataset.waPrefix !== undefined) {
    offset += node.dataset.waPrefix.length;
  }

  for (let index = 0; index < childOffset && index < node.childNodes.length; index += 1) {
    offset += getNodeRawLength(node.childNodes[index]);
  }

  return offset;
}

function getRawOffsetFromDomPosition(root, targetNode, targetOffset) {
  let result = null;

  const walk = (node, offset) => {
    if (result !== null) return offset;

    if (node === targetNode) {
      result = offset + getOffsetInsideNode(node, targetOffset);
      return offset + getNodeRawLength(node);
    }

    if (node.nodeType === Node.TEXT_NODE || (node.dataset && node.dataset.ph) || node.nodeName === "BR") {
      return offset + getNodeRawLength(node);
    }

    let currentOffset = offset;
    if (node.dataset && node.dataset.waPrefix !== undefined) {
      currentOffset += node.dataset.waPrefix.length;
    }

    node.childNodes.forEach((child) => {
      currentOffset = walk(child, currentOffset);
    });

    if (node.dataset && node.dataset.waPrefix !== undefined) {
      currentOffset += (node.dataset.waSuffix || "").length;
    }

    return currentOffset;
  };

  walk(root, 0);
  return result === null ? getNodeRawLength(root) : result;
}

function getElementEdgePosition(element, atEnd) {
  return {
    node: element,
    offset: atEnd ? element.childNodes.length : 0
  };
}

function getDomPositionForRawOffset(root, rawOffset) {
  const targetOffset = Math.max(0, rawOffset);
  let result = null;

  const walk = (node, offset) => {
    if (result) return offset;

    if (node.nodeType === Node.TEXT_NODE) {
      const length = (node.textContent || "").length;
      if (targetOffset <= offset + length) {
        result = { node, offset: Math.max(0, Math.min(length, targetOffset - offset)) };
      }
      return offset + length;
    }

    if (node.nodeName === "BR") {
      const parent = node.parentNode || root;
      const childIndex = getChildIndex(node);
      if (targetOffset <= offset + 1) {
        result = { node: parent, offset: targetOffset <= offset ? childIndex : childIndex + 1 };
      }
      return offset + 1;
    }

    if (node.dataset && node.dataset.ph) {
      const length = node.dataset.ph.length;
      const parent = node.parentNode || root;
      const childIndex = getChildIndex(node);
      if (targetOffset <= offset + length) {
        result = { node: parent, offset: targetOffset <= offset ? childIndex : childIndex + 1 };
      }
      return offset + length;
    }

    let currentOffset = offset;
    if (node.dataset && node.dataset.waPrefix !== undefined) {
      const prefixLength = node.dataset.waPrefix.length;
      if (targetOffset <= currentOffset + prefixLength) {
        result = getElementEdgePosition(node, false);
        return currentOffset + getNodeRawLength(node);
      }
      currentOffset += prefixLength;
    }

    for (const child of Array.from(node.childNodes)) {
      const childLength = getNodeRawLength(child);
      if (targetOffset <= currentOffset + childLength) {
        return walk(child, currentOffset);
      }
      currentOffset += childLength;
    }

    if (node.dataset && node.dataset.waPrefix !== undefined) {
      const suffixLength = (node.dataset.waSuffix || "").length;
      if (targetOffset <= currentOffset + suffixLength) {
        result = getElementEdgePosition(node, true);
      }
      currentOffset += suffixLength;
    }

    return currentOffset;
  };

  walk(root, 0);
  return result || getElementEdgePosition(root, true);
}

class ProviderMessageRichTextEditor extends Component {
  constructor(props) {
    super(props);
    this.ref = createRef();
    this._ignoreNextInput = false;
    this.lastSelectionOffsets = null;
    this.lastSelectionHasPlaceholder = false;
    this.undoStack = [];
    this.redoStack = [];

    this.state = {
      emojiAnchorEl: null
    };
  }

  hasAllRequiredPlaceholders = (text) => {
    const required = this.props.requiredPlaceholders || [];
    if (required.length === 0) return true;

    const normalized = text || "";
    return required.every((placeholder) => normalized.includes(placeholder));
  };

  componentDidMount() {
    this.updateHTML(this.props.value);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && this._ignoreNextInput === false) {
      const offsets = this.getCurrentSelectionRawOffsets();
      this.updateHTML(this.props.value);
      this.undoStack = [];
      this.redoStack = [];
      if (offsets !== null) {
        const valueLength = (this.props.value || "").length;
        this.setSelectionRawOffsets(
          Math.min(offsets.start, valueLength),
          Math.min(offsets.end, valueLength)
        );
      }
    }
  }

  updateHTML(value) {
    if (this.ref.current) {
      this.ref.current.innerHTML = buildHTML(value || "", this.props.placeholderDescriptions || {});
    }
  }

  getEditorText = () => {
    return this.ref.current ? extractText(this.ref.current) : (this.props.value || "");
  };

  getCurrentSelectionRawOffsets = () => {
    const el = this.ref.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return null;

    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) {
      return null;
    }

    const start = getRawOffsetFromDomPosition(el, range.startContainer, range.startOffset);
    const end = getRawOffsetFromDomPosition(el, range.endContainer, range.endOffset);
    return start <= end ? { start, end } : { start: end, end: start };
  };

  saveSelectionOffsets = () => {
    const offsets = this.getCurrentSelectionRawOffsets();
    if (offsets) {
      this.lastSelectionOffsets = offsets;
      this.lastSelectionHasPlaceholder = this.selectionContainsPlaceholder();
    }
  };

  getSelectionOffsetsForAction = () => {
    const currentOffsets = this.getCurrentSelectionRawOffsets();
    if (currentOffsets) {
      this.lastSelectionOffsets = currentOffsets;
      return currentOffsets;
    }

    if (this.lastSelectionOffsets) {
      return this.lastSelectionOffsets;
    }

    const textLength = this.getEditorText().length;
    return { start: textLength, end: textLength };
  };

  setSelectionRawOffsets(start, end = start) {
    const el = this.ref.current;
    const sel = window.getSelection();
    if (!el || !sel) return;

    if (document.activeElement !== el) {
      el.focus();
    }

    const startPosition = getDomPositionForRawOffset(el, start);
    const endPosition = getDomPositionForRawOffset(el, end);
    const range = document.createRange();
    range.setStart(startPosition.node, startPosition.offset);
    range.setEnd(endPosition.node, endPosition.offset);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  selectionContainsPlaceholder = () => {
    const el = this.ref.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return false;

    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) {
      return false;
    }

    return !range.collapsed && !!range.cloneContents().querySelector("[data-ph]");
  };

  createHistorySnapshot = () => {
    const text = this.getEditorText();
    const selection = this.getCurrentSelectionRawOffsets() ||
      this.lastSelectionOffsets ||
      { start: text.length, end: text.length };

    return {
      text,
      selection: {
        start: Math.max(0, Math.min(selection.start, text.length)),
        end: Math.max(0, Math.min(selection.end, text.length))
      }
    };
  };

  areHistorySnapshotsEqual = (first, second) => {
    return !!first &&
      !!second &&
      first.text === second.text &&
      first.selection?.start === second.selection?.start &&
      first.selection?.end === second.selection?.end;
  };

  pushHistorySnapshot = (stack, snapshot) => {
    if (!snapshot) return;

    const lastSnapshot = stack[stack.length - 1];
    if (this.areHistorySnapshotsEqual(lastSnapshot, snapshot)) {
      return;
    }

    stack.push(snapshot);
    if (stack.length > HISTORY_LIMIT) {
      stack.shift();
    }
  };

  pushUndoSnapshot = (snapshot = this.createHistorySnapshot()) => {
    this.pushHistorySnapshot(this.undoStack, snapshot);
    this.redoStack = [];
  };

  applyHistorySnapshot = (snapshot) => {
    if (!snapshot) return;

    const text = snapshot.text || "";
    const selection = snapshot.selection || { start: text.length, end: text.length };

    this._ignoreNextInput = true;
    this.updateHTML(text);
    this.props.onChange(text);

    setTimeout(() => {
      const safeStart = Math.max(0, Math.min(selection.start, text.length));
      const safeEnd = Math.max(0, Math.min(selection.end, text.length));
      this.setSelectionRawOffsets(safeStart, safeEnd);
      this.lastSelectionOffsets = { start: safeStart, end: safeEnd };
      this.lastSelectionHasPlaceholder = false;
      this._ignoreNextInput = false;
    }, 0);
  };

  restoreHistory = (direction) => {
    const sourceStack = direction === "redo" ? this.redoStack : this.undoStack;
    const targetStack = direction === "redo" ? this.undoStack : this.redoStack;

    if (sourceStack.length === 0) {
      return;
    }

    const currentSnapshot = this.createHistorySnapshot();
    const nextSnapshot = sourceStack.pop();
    this.pushHistorySnapshot(targetStack, currentSnapshot);
    this.applyHistorySnapshot(nextSnapshot);
  };

  commitEditorValue = (nextText, selectionStart, selectionEnd = selectionStart) => {
    const currentText = this.getEditorText();
    if (this.hasAllRequiredPlaceholders(currentText) && !this.hasAllRequiredPlaceholders(nextText)) {
      this._ignoreNextInput = true;
      this.updateHTML(currentText);
      this.setSelectionRawOffsets(Math.min(selectionStart, currentText.length));
      setTimeout(() => { this._ignoreNextInput = false; }, 0);
      return;
    }

    this._ignoreNextInput = true;
    this.updateHTML(nextText);
    this.props.onChange(nextText);

    setTimeout(() => {
      const safeStart = Math.max(0, Math.min(selectionStart, nextText.length));
      const safeEnd = Math.max(0, Math.min(selectionEnd, nextText.length));
      this.setSelectionRawOffsets(safeStart, safeEnd);
      this.lastSelectionOffsets = { start: safeStart, end: safeEnd };
      this.lastSelectionHasPlaceholder = false;
      this._ignoreNextInput = false;
    }, 0);
  };

  insertPlainText = (text) => {
    if (!text || this.props.disabled) return;

    const currentText = this.getEditorText();
    const currentOffsets = this.getCurrentSelectionRawOffsets();
    const hasPlaceholder = currentOffsets
      ? this.selectionContainsPlaceholder()
      : this.lastSelectionHasPlaceholder;
    if (hasPlaceholder) return;

    const fallbackOffset = currentText.length;
    const { start, end } = currentOffsets || this.lastSelectionOffsets || { start: fallbackOffset, end: fallbackOffset };
    const nextText = `${currentText.slice(0, start)}${text}${currentText.slice(end)}`;
    const nextCaret = start + text.length;
    this.pushUndoSnapshot({ text: currentText, selection: { start, end } });
    this.commitEditorValue(nextText, nextCaret);
  };

  applyFormat = (prefix, suffix) => {
    if (this.props.disabled) return;

    const currentText = this.getEditorText();
    const { start, end } = this.getSelectionOffsetsForAction();
    const hasSelection = start !== end;
    const selectedText = currentText.slice(start, end);
    const hasWrappingMarkers =
      hasSelection &&
      start >= prefix.length &&
      currentText.slice(start - prefix.length, start) === prefix &&
      currentText.slice(end, end + suffix.length) === suffix;

    this.pushUndoSnapshot({ text: currentText, selection: { start, end } });

    if (hasWrappingMarkers) {
      const nextText = `${currentText.slice(0, start - prefix.length)}${selectedText}${currentText.slice(end + suffix.length)}`;
      const nextStart = start - prefix.length;
      const nextEnd = end - prefix.length;
      this.commitEditorValue(nextText, nextStart, nextEnd);
      return;
    }

    const nextText = `${currentText.slice(0, start)}${prefix}${selectedText}${suffix}${currentText.slice(end)}`;
    const nextStart = start + prefix.length;
    const nextEnd = nextStart + selectedText.length;
    this.commitEditorValue(nextText, nextStart, hasSelection ? nextEnd : nextStart);
  };

  handleEmojiButtonClick = (event) => {
    if (this.props.disabled) return;
    this.saveSelectionOffsets();
    this.setState({ emojiAnchorEl: event.currentTarget });
  };

  handleEmojiClose = () => {
    this.setState({ emojiAnchorEl: null });
  };

  handleEmojiClick = (emojiObject) => {
    const emoji = emojiObject?.emoji || "";
    if (emoji) {
      this.insertPlainText(emoji);
    }
    this.handleEmojiClose();
  };

  handleBeforeInput = () => {
    if (this.props.disabled || this._ignoreNextInput) return;
    this.pushUndoSnapshot();
  };

  handleKeyDown = (e) => {
    const commandKey = e.ctrlKey || e.metaKey;
    const key = e.key ? e.key.toLowerCase() : "";

    if (commandKey && key === "z") {
      e.preventDefault();
      this.restoreHistory(e.shiftKey ? "redo" : "undo");
      return;
    }

    if (commandKey && key === "y") {
      e.preventDefault();
      this.restoreHistory("redo");
      return;
    }

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    const isCollapsed = range.collapsed;
    const selectionHasChip = !isCollapsed && !!range.cloneContents().querySelector("[data-ph]");

    if (commandKey && key === "b") {
      e.preventDefault();
      this.applyFormat("*", "*");
      return;
    }

    if (commandKey && key === "i") {
      e.preventDefault();
      this.applyFormat("_", "_");
      return;
    }

    // Helper: get the nearest ph-chip touching the caret
    const adjacentChip = (direction) => {
      if (!isCollapsed) return null;
      const { anchorNode, anchorOffset } = sel;
      if (direction === "backward") {
        // chip immediately before caret?
        if (anchorOffset === 0) {
          const prev = anchorNode.previousSibling;
          if (prev && prev.dataset && prev.dataset.ph) return prev;
        }
      } else {
        // chip immediately after caret?
        const textLen = anchorNode.textContent ? anchorNode.textContent.length : 0;
        if (anchorOffset === textLen || anchorNode.nodeType !== Node.TEXT_NODE) {
          const next = anchorNode.nextSibling;
          if (next && next.dataset && next.dataset.ph) return next;
        }
      }
      return null;
    };

    // If a selection contains any placeholder chip, block direct replacement typing.
    if (selectionHasChip) {
      const isPrintable = e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (isPrintable || e.key === "Enter") {
        e.preventDefault();
        return;
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      this.insertPlainText("\n");
      return;
    }

    if (e.key === "Backspace") {
      // Check if selection contains a chip
      if (!isCollapsed) {
        const fragment = range.cloneContents();
        const hasChip = fragment.querySelector("[data-ph]");
        if (hasChip) { e.preventDefault(); return; }
      } else if (adjacentChip("backward")) {
        e.preventDefault();
        return;
      }
    }

    if (e.key === "Delete") {
      if (!isCollapsed) {
        const fragment = range.cloneContents();
        const hasChip = fragment.querySelector("[data-ph]");
        if (hasChip) { e.preventDefault(); return; }
      } else if (adjacentChip("forward")) {
        e.preventDefault();
        return;
      }
    }

    // Prevent cutting a selection that contains chips
    if ((e.key === "x" && (e.ctrlKey || e.metaKey)) && !isCollapsed) {
      const fragment = range.cloneContents();
      if (fragment.querySelector("[data-ph]")) { e.preventDefault(); return; }
      this.pushUndoSnapshot();
    }
  };

  handleInput = () => {
    if (!this.ref.current) return;
    const currentText = this.props.value || "";
    const nextText = extractText(this.ref.current);
    const offsets = this.getCurrentSelectionRawOffsets();

    // Hard guarantee: once required placeholders are present, they cannot be removed.
    if (this.hasAllRequiredPlaceholders(currentText) && !this.hasAllRequiredPlaceholders(nextText)) {
      const caretOffset = offsets ? offsets.start : currentText.length;
      this._ignoreNextInput = true;
      this.updateHTML(currentText);
      this.setSelectionRawOffsets(Math.min(caretOffset, currentText.length));
      setTimeout(() => { this._ignoreNextInput = false; }, 0);
      return;
    }

    this._ignoreNextInput = true;
    this.updateHTML(nextText);
    if (offsets) {
      const safeStart = Math.max(0, Math.min(offsets.start, nextText.length));
      const safeEnd = Math.max(0, Math.min(offsets.end, nextText.length));
      this.setSelectionRawOffsets(safeStart, safeEnd);
      this.lastSelectionOffsets = { start: safeStart, end: safeEnd };
      this.lastSelectionHasPlaceholder = false;
    }
    this.props.onChange(nextText);
    setTimeout(() => { this._ignoreNextInput = false; }, 0);
  };

  handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    this.insertPlainText(text);
  };

  handleCut = (e) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    if (range.collapsed) return;

    const hasChip = !!range.cloneContents().querySelector("[data-ph]");
    if (hasChip) {
      e.preventDefault();
      return;
    }

    this.pushUndoSnapshot();
  };

  handleDrop = (e) => {
    e.preventDefault();
  };

  renderToolbarButton = (title, icon, onClick) => {
    const { disabled } = this.props;

    return (
      <Tooltip title={title}>
        <span>
          <IconButton
            size="small"
            className="wa-editor-action"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onClick}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  render() {
    const { error, disabled, placeholder } = this.props;
    const emojiOpen = Boolean(this.state.emojiAnchorEl);

    return (
      <div style={{ position: "relative" }}>
        <style>{`
          .ph-editor-wrap {
            border: 1px solid ${error ? "var(--status-error)" : "var(--border-color)"};
            border-radius: 6px;
            transition: border-color 0.2s, box-shadow 0.2s;
            background: var(--mexx-bg-secondary);
          }
          .wa-editor-toolbar {
            min-height: 38px;
            padding: 4px 6px;
            display: flex;
            align-items: center;
            gap: 4px;
            border-bottom: 1px solid var(--border-color);
            background: var(--mexx-bg-surface);
            border-radius: 6px 6px 0 0;
          }
          .wa-editor-action {
            width: 30px;
            height: 30px;
            color: var(--text-primary);
          }
          .wa-editor-action:hover {
            background: var(--mexx-green-subtle);
            color: var(--mexx-green-primary);
          }
          .ph-editor-wrap:focus-within {
            border-color: ${error ? "var(--status-error)" : "var(--mexx-green-primary)"};
            box-shadow: 0 0 0 2px ${error ? "var(--status-error-bg)" : "var(--mexx-green-subtle)"};
          }
          .ph-editor {
            min-height: 96px;
            padding: 12px 14px;
            font-size: 14px;
            font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
            line-height: 1.7;
            outline: none;
            white-space: pre-wrap;
            word-break: break-word;
            color: var(--text-primary);
          }
          .ph-editor:empty:before {
            content: attr(data-placeholder);
            color: var(--text-muted);
            pointer-events: none;
          }
          .ph-chip {
            display: inline-flex;
            align-items: center;
            background: var(--mexx-green-subtle);
            color: var(--mexx-green-primary);
            border: 1px solid var(--border-color-focus);
            border-radius: 4px;
            padding: 1px 6px;
            font-size: 12.5px;
            font-family: 'JetBrains Mono', 'Fira Code', monospace;
            font-weight: 600;
            margin: 0 1px;
            cursor: not-allowed;
            user-select: none;
            vertical-align: middle;
          }
          .wa-format-bold {
            font-weight: 700;
          }
          .wa-format-italic {
            font-style: italic;
          }
          .wa-format-strike {
            text-decoration: line-through;
          }
          .wa-format-code {
            display: inline;
            padding: 1px 4px;
            border-radius: 4px;
            background: var(--mexx-bg-surface);
            border: 1px solid var(--border-color);
            font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
            font-size: 13px;
          }
        `}</style>
        <div className="ph-editor-wrap">
          <div className="wa-editor-toolbar">
            {this.renderToolbarButton("Negrito", <FormatBoldIcon fontSize="small" />, () => this.applyFormat("*", "*"))}
            {this.renderToolbarButton("Italico", <FormatItalicIcon fontSize="small" />, () => this.applyFormat("_", "_"))}
            {this.renderToolbarButton("Tachado", <StrikethroughSIcon fontSize="small" />, () => this.applyFormat("~", "~"))}
            {this.renderToolbarButton("Monoespacado", <CodeIcon fontSize="small" />, () => this.applyFormat("```", "```"))}
            <Tooltip title="Emoji">
              <span>
                <IconButton
                  size="small"
                  className="wa-editor-action"
                  disabled={disabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={this.handleEmojiButtonClick}
                >
                  <InsertEmoticonIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
          <div
            ref={this.ref}
            className="ph-editor"
            contentEditable={!disabled}
            suppressContentEditableWarning
            data-placeholder={placeholder || "Digite a mensagem..."}
            onBeforeInput={this.handleBeforeInput}
            onKeyDown={this.handleKeyDown}
            onKeyUp={this.saveSelectionOffsets}
            onMouseUp={this.saveSelectionOffsets}
            onFocus={this.saveSelectionOffsets}
            onInput={this.handleInput}
            onPaste={this.handlePaste}
            onCut={this.handleCut}
            onDrop={this.handleDrop}
          />
        </div>
        <Popover
          open={emojiOpen}
          anchorEl={this.state.emojiAnchorEl}
          onClose={this.handleEmojiClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box sx={{ p: 1 }}>
            <Picker onEmojiClick={this.handleEmojiClick} />
          </Box>
        </Popover>
        {error && (
          <Typography variant="caption" style={{ color: "var(--status-error)", marginTop: 4, display: "block", paddingLeft: 2 }}>
            {error}
          </Typography>
        )}
      </div>
    );
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Main page component
// ──────────────────────────────────────────────────────────────────────────────
class ProviderPredefinedMessageTemplates extends Component {
  constructor(props) {
    super(props);

    this.isAdmGlobal = false;

    const { userSession } = this.props;

    if (userSession == null || userSession.user == null || userSession.user.userrole == null) {
      Api.logoff();
    } else {
      userSession.user.userrole.map((item) => {
        if (item.role.roletype == 0) {
          this.isAdmGlobal = true;
        }
      });
    }

    this.state = {
      loading: false,
      saving: false,
      openNotification: false,
      notificationVariant: "error",
      notificationMessage: "",
      items: [],
      initialMessagesById: {},
      fieldErrors: {},
      activeCategory: "Todos"
    };
  }

  componentDidMount() {
    this.loadConfiguration();
  }

  extractErrorMessage = (resultOrError, fallbackMessage) => {
    if (resultOrError?.errors?.length > 0) return resultOrError.errors[0];
    if (resultOrError?.response?.data?.errors?.length > 0) return resultOrError.response.data.errors[0];
    if (resultOrError?.data?.errors?.length > 0) return resultOrError.data.errors[0];
    return fallbackMessage;
  };

  getMissingPlaceholders = (requiredPlaceholders, message) => {
    const normalizedMessage = message || "";
    return (requiredPlaceholders || []).filter((p) => !normalizedMessage.includes(p));
  };

  getFieldError = (item) => {
    const missing = this.getMissingPlaceholders(item.requiredPlaceholders, item.message);
    return missing.length === 0 ? "" : `Placeholders obrigatórios ausentes: ${missing.join(", ")}`;
  };

  buildFieldErrors = (items) => {
    const errors = {};
    (items || []).forEach((item) => {
      const msg = this.getFieldError(item);
      if (msg) errors[item.id] = msg;
    });
    return errors;
  };

  buildInitialMessagesById = (items) => {
    const map = {};
    (items || []).forEach((item) => { map[item.id] = item.message || ""; });
    return map;
  };

  canViewSatisfactionSurveyLinkV2Messages = () => {
    return Api != null && (Api.getSassHirerId() == 1011 || Api.getSassHirerId() == 15 || Api.getSassHirerId() == 1);
  };

  isSatisfactionSurveyLinkV2Message = (id) => {
    return [20, 21].includes(Number(id));
  };

  loadConfiguration = async () => {
    this.setState({ loading: true });
    try {
      const result = await getProviderPredefinedMessageTemplates();
      if (result?.success) {
        const apiItems = Array.isArray(result.data) ? result.data : [];
        const canViewSatisfactionSurveyLinkV2Messages = this.canViewSatisfactionSurveyLinkV2Messages();
        const items = apiItems
          .filter((apiItem) =>
            canViewSatisfactionSurveyLinkV2Messages ||
            !this.isSatisfactionSurveyLinkV2Message(apiItem?.id))
          .map((apiItem) => {
            const id = Number(apiItem?.id || 0);
            if (id <= 0) {
              return null;
            }

            return {
              id,
              grouptype: Number(apiItem?.grouptype || 0),
              groupkey: typeof apiItem?.groupkey === "string" ? apiItem.groupkey : "",
              grouplabel: typeof apiItem?.grouplabel === "string" ? apiItem.grouplabel : "",
              messageusagetype: Number(apiItem?.messageusagetype || 1),
              messageusagekey: typeof apiItem?.messageusagekey === "string" ? apiItem.messageusagekey : "Template",
              messageusagelabel: typeof apiItem?.messageusagelabel === "string" ? apiItem.messageusagelabel : "Template WhatsApp",
              label: apiItem?.label || `Mensagem ${id}`,
              message: typeof apiItem?.message === "string" ? apiItem.message : "",
              requiredPlaceholders: Array.isArray(apiItem?.requiredplaceholders)
                ? apiItem.requiredplaceholders
                : []
            };
          })
          .filter((item) => item !== null)
          .sort((a, b) => a.id - b.id);

        this.setState({
          items,
          initialMessagesById: this.buildInitialMessagesById(items),
          fieldErrors: this.buildFieldErrors(items),
          loading: false
        });
        return;
      }
      this.setState({
        loading: false,
        openNotification: true,
        notificationVariant: "error",
        notificationMessage: this.extractErrorMessage(result, "Não foi possível carregar as mensagens.")
      });
    } catch (error) {
      this.setState({
        loading: false,
        openNotification: true,
        notificationVariant: "error",
        notificationMessage: this.extractErrorMessage(error, "Não foi possível carregar as mensagens.")
      });
      Api.kickoff(error);
    }
  };

  handleMessageChange = (id, message) => {
    this.setState((prev) => {
      const items = prev.items.map((item) =>
        item.id === id ? { ...item, message } : item
      );
      return { items, fieldErrors: this.buildFieldErrors(items) };
    });
  };

  closeNotification = () => this.setState({ openNotification: false });

  canSave = () => {
    const { items, fieldErrors, loading, saving } = this.state;
    return (
      !loading &&
      !saving &&
      items.length > 0 &&
      Object.keys(fieldErrors).length === 0 &&
      this.getChangedItems().length > 0
    );
  };

  getChangedItems = () =>
    this.state.items
      .filter((item) => (this.state.initialMessagesById[item.id] || "") !== (item.message || ""))
      .map(({ id, message }) => ({ id, message }));

  handleSubmit = async () => {
    const fieldErrors = this.buildFieldErrors(this.state.items);
    if (Object.keys(fieldErrors).length > 0) {
      this.setState({ fieldErrors, openNotification: true, notificationVariant: "error", notificationMessage: "Corrija os placeholders obrigatórios antes de salvar." });
      return;
    }
    const payload = this.getChangedItems();
    if (payload.length === 0) {
      this.setState({ openNotification: true, notificationVariant: "success", notificationMessage: "Nenhuma alteração encontrada para salvar." });
      return;
    }
    this.setState({ saving: true });
    try {
      const result = await updateProviderPredefinedMessageTemplates(payload);
      if (result?.success) {
        const initialMessagesById = { ...this.state.initialMessagesById };
        payload.forEach((item) => { initialMessagesById[item.id] = item.message; });
        this.setState({ saving: false, initialMessagesById, openNotification: true, notificationVariant: "success", notificationMessage: "Mensagens atualizadas com sucesso." });
        return;
      }
      this.setState({ saving: false, openNotification: true, notificationVariant: "error", notificationMessage: this.extractErrorMessage(result, "Não foi possível salvar as mensagens.") });
    } catch (error) {
      this.setState({ saving: false, openNotification: true, notificationVariant: "error", notificationMessage: this.extractErrorMessage(error, "Não foi possível salvar as mensagens.") });
      Api.kickoff(error);
    }
  };

  render() {
    const { classes, headerMenu, actionsHeaderMenu } = this.props;
    const { items, fieldErrors, loading, saving, activeCategory } = this.state;

    if (!this.isAdmGlobal) {
      const language = this.props.match.params.lang;
      document.location.href = `/${language}/unavailable`;
    }

    if (Api == null) {
      const language = this.props.match.params.lang;
      document.location.href = `/${language}/unavailable`;
    }

    const groupMetaByKey = items.reduce((acc, item) => {
      if (!item.groupkey) {
        return acc;
      }

      const existing = acc[item.groupkey];
      const nextGroupType = Number(item.grouptype || 0);
      if (!existing || nextGroupType < existing.grouptype) {
        acc[item.groupkey] = {
          groupkey: item.groupkey,
          grouptype: nextGroupType,
          grouplabel: getProviderGroupLabel(item.groupkey, item.grouplabel || item.groupkey)
        };
      }

      return acc;
    }, {});

    const sortedGroups = Object.values(groupMetaByKey)
      .sort((a, b) => {
        if (a.grouptype !== b.grouptype) {
          return a.grouptype - b.grouptype;
        }

        return (a.grouplabel || "").localeCompare(b.grouplabel || "");
      });

    const categories = ["Todos", ...sortedGroups.map((group) => group.groupkey)];
    const visibleItems = activeCategory === "Todos"
      ? items
      : items.filter((item) => item.groupkey === activeCategory);

    const changedCount = this.getChangedItems().length;
    const errorCount = Object.keys(fieldErrors).length;
    const progress = items.length > 0
      ? Math.round((items.filter((i) => (i.message || "").trim().length > 0).length / items.length) * 100)
      : 0;

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />

        <main
          className={clsx(
            classes.content,
            { [classes.contentShift]: headerMenu.open },
            classes.actionsContent,
            { [classes.actionscontentShift]: actionsHeaderMenu.open }
          )}
        >
          <div className={classes.drawerHeader} />

          <Container component="main" maxWidth="xl" style={{ maxWidth: "100%", paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />

            {/* ── Header ─────────────────────────────────────────── */}
            <Box
              sx={{
                borderRadius: "12px",
                p: 3,
                mb: 3,
                color: "var(--text-primary)",
                backgroundColor: "var(--mexx-bg-elevated)",
                border: "var(--border-default)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                boxShadow: "var(--shadow-sm)"
              }}
            >
              <Box>
                <Typography variant="h5" style={{ fontWeight: 700, letterSpacing: "-0.3px" }}>
                  Mensagens de Modelo do Provedor
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.8, marginTop: 4 }}>
                  Edite os textos e mantenha os placeholders obrigatórios — eles ficam destacados e não podem ser removidos.
                </Typography>

                {/* Progress bar */}
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      width: 180,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "var(--mexx-green-subtle)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "var(--mexx-green-primary)",
                        borderRadius: 3
                      }
                    }}
                  />
                  <Typography variant="caption" style={{ opacity: 0.85 }}>
                    {progress}% preenchido
                  </Typography>
                  {errorCount > 0 && (
                    <Chip
                      icon={<ErrorOutlineIcon style={{ fontSize: 14, color: "var(--text-on-primary)" }} />}
                      label={`${errorCount} erro${errorCount > 1 ? "s" : ""}`}
                      size="small"
                      sx={{ bgcolor: "var(--status-error)", color: "var(--text-on-primary)", height: 22, fontSize: 11 }}
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Tooltip title="Recarregar configurações">
                  <span>
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<RefreshIcon />}
                      disabled={loading || saving}
                      onClick={this.loadConfiguration}
                      sx={{
                        color: "var(--mexx-green-primary)",
                        borderColor: "var(--mexx-green-primary)",
                        "&:hover": {
                          borderColor: "var(--mexx-green-primary)",
                          backgroundColor: "var(--mexx-green-subtle)"
                        }
                      }}
                    >
                      Recarregar
                    </Button>
                  </span>
                </Tooltip>

                <Badge badgeContent={changedCount || null} color="warning">
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    disabled={!this.canSave()}
                    onClick={this.handleSubmit}
                    sx={{
                      bgcolor: "var(--mexx-green-primary)",
                      color: "var(--text-on-primary)",
                      fontWeight: 700,
                      "&:hover": { bgcolor: "var(--mexx-green-dark)" },
                      "&:disabled": { bgcolor: "var(--mexx-gray-300)", color: "var(--text-muted)" }
                    }}
                  >
                    Salvar
                  </Button>
                </Badge>
              </Box>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

            {/* ── Category filter tabs ───────────────────────────── */}
            <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
              {categories.map((cat) => {
                const isActive = activeCategory === cat;
                const groupMeta = cat === "Todos" ? null : groupMetaByKey[cat];
                const label = cat === "Todos" ? "Todos" : (groupMeta?.grouplabel || getProviderGroupLabel(cat, cat));
                const count = cat === "Todos" ? items.length : items.filter((i) => i.groupkey === cat).length;
                const errorsInCat = cat === "Todos"
                  ? errorCount
                  : items.filter((i) => i.groupkey === cat && fieldErrors[i.id]).length;
                return (
                  <Chip
                    key={cat}
                    label={`${label} (${count})`}
                    onClick={() => this.setState({ activeCategory: cat })}
                    icon={errorsInCat > 0 ? <ErrorOutlineIcon style={{ color: isActive ? "var(--text-on-primary)" : "var(--status-error)", fontSize: 15 }} /> : undefined}
                    sx={{
                      bgcolor: isActive ? "var(--mexx-green-primary)" : "transparent",
                      color: isActive ? "var(--text-on-primary)" : "var(--text-primary)",
                      border: "1.5px solid",
                      borderColor: isActive ? "var(--mexx-green-primary)" : "var(--border-color)",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 13,
                      "&:hover": {
                        bgcolor: isActive ? "var(--mexx-green-primary)" : "var(--mexx-green-subtle)"
                      },
                      transition: "all 0.15s"
                    }}
                  />
                );
              })}
            </Box>

            {/* ── Field list ────────────────────────────────────── */}
            <Grid container spacing={2}>
              {visibleItems.map((item) => {
                const error = fieldErrors[item.id];
                const isChanged = (this.state.initialMessagesById[item.id] || "") !== (item.message || "");
                const catLabel = getProviderGroupLabel(item.groupkey, item.grouplabel || item.groupkey);
                const isFlowMessage = item.messageusagetype === 2;
                const usageLabel = item.messageusagelabel || (isFlowMessage ? "Mensagem do fluxo" : "Template WhatsApp");
                const placeholderDescriptions = getPlaceholderDescriptions(item.id);

                return (
                  <Grid item xs={12} md={6} key={item.id}>
                    <Card
                      sx={{
                        borderRadius: "10px",
                        border: error
                          ? "1.5px solid var(--status-error)"
                          : isChanged
                          ? "1.5px solid var(--mexx-green-primary)"
                          : "1.5px solid var(--border-color)",
                        boxShadow: "var(--shadow-xs)",
                        transition: "border 0.2s, box-shadow 0.2s",
                        height: "100%"
                      }}
                    >
                      <CardContent sx={{ pb: "16px !important" }}>
                        {/* Card header row */}
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1, flexWrap: "wrap" }}>
                          <Box
                            sx={{
                              width: 28, height: 28, borderRadius: "6px",
                              bgcolor: "var(--mexx-green-primary)", color: "var(--text-on-primary)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 11, fontWeight: 700, flexShrink: 0
                            }}
                          >
                            {item.id}
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, color: "var(--text-primary)", lineHeight: 1.3 }}>
                            {item.label}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                            <Chip
                              label={catLabel}
                              size="small"
                              sx={{ bgcolor: "var(--mexx-bg-surface)", color: "var(--text-primary)", fontWeight: 600, fontSize: 10, height: 20 }}
                            />
                            <Chip
                              label={usageLabel}
                              size="small"
                              sx={{
                                bgcolor: isFlowMessage ? "var(--status-warning-bg)" : "var(--mexx-green-subtle)",
                                color: isFlowMessage ? "var(--status-warning)" : "var(--mexx-green-primary)",
                                border: "1px solid",
                                borderColor: isFlowMessage ? "var(--status-warning)" : "var(--mexx-green-primary)",
                                fontWeight: 600,
                                fontSize: 10,
                                height: 20
                              }}
                            />
                            {isChanged && (
                              <Chip
                                label="Editado"
                                size="small"
                                sx={{
                                  bgcolor: "var(--mexx-green-subtle)",
                                  color: "var(--mexx-green-primary)",
                                  border: "1px solid var(--mexx-green-primary)",
                                  fontWeight: 600,
                                  fontSize: 10,
                                  height: 20
                                }}
                              />
                            )}
                            {error ? (
                              <ErrorOutlineIcon sx={{ color: "var(--status-error)", fontSize: 18 }} />
                            ) : item.message ? (
                              <CheckCircleOutlineIcon sx={{ color: "var(--mexx-green-primary)", fontSize: 18 }} />
                            ) : null}
                          </Box>
                        </Box>

                        <Divider sx={{ mb: 1.5 }} />

                        {/* Placeholder badges */}
                        {item.requiredPlaceholders.length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                            {item.requiredPlaceholders.map((ph) => {
                              const description = getPlaceholderDescription(placeholderDescriptions, ph);
                              const chip = (
                                <Chip
                                  label={ph}
                                  size="small"
                                  sx={{
                                    bgcolor: "var(--mexx-green-primary)",
                                    color: "var(--text-on-primary)",
                                    border: "1px solid",
                                    borderColor: "var(--mexx-green-primary)",
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    fontSize: 11,
                                    height: 22
                                  }}
                                />
                              );

                              return (
                                <Tooltip key={ph} title={description || ph}>
                                  <span>{chip}</span>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        )}

                        {/* Editor */}
                        <ProviderMessageRichTextEditor
                          value={item.message}
                          requiredPlaceholders={item.requiredPlaceholders}
                          placeholderDescriptions={placeholderDescriptions}
                          error={error}
                          disabled={loading || saving}
                          onChange={(text) => this.handleMessageChange(item.id, text)}
                          placeholder={`Digite a mensagem para "${item.label}"...`}
                        />

                        {!error && item.requiredPlaceholders.length === 0 && (
                          <Typography variant="caption" sx={{ color: "var(--text-muted)", mt: 0.5, display: "block" }}>
                            Sem placeholders obrigatórios.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* ── Bottom action bar (mirror) ───────────────────── */}
            <Box
              sx={{
                borderRadius: "12px",
                p: 2,
                mt: 3,
                mb: 1,
                color: "var(--text-primary)",
                backgroundColor: "var(--mexx-bg-elevated)",
                border: "var(--border-default)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                boxShadow: "var(--shadow-sm)"
              }}
            >
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  Mensagens de Modelo do Provedor
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.8, marginTop: 2, display: "block" }}>
                  Revise os campos e salve as alterações.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Tooltip title="Recarregar configurações">
                  <span>
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<RefreshIcon />}
                      disabled={loading || saving}
                      onClick={this.loadConfiguration}
                      sx={{
                        color: "var(--mexx-green-primary)",
                        borderColor: "var(--mexx-green-primary)",
                        "&:hover": {
                          borderColor: "var(--mexx-green-primary)",
                          backgroundColor: "var(--mexx-green-subtle)"
                        }
                      }}
                    >
                      Recarregar
                    </Button>
                  </span>
                </Tooltip>

                <Badge badgeContent={changedCount || null} color="warning">
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    disabled={!this.canSave()}
                    onClick={this.handleSubmit}
                    sx={{
                      bgcolor: "var(--mexx-green-primary)",
                      color: "var(--text-on-primary)",
                      fontWeight: 700,
                      "&:hover": { bgcolor: "var(--mexx-green-dark)" },
                      "&:disabled": { bgcolor: "var(--mexx-gray-300)", color: "var(--text-muted)" }
                    }}
                  >
                    Salvar
                  </Button>
                </Badge>
              </Box>
            </Box>

            {(loading || saving) && (
              <Overlay>
                <CircularProgress sx={{ color: "var(--mexx-green-primary)" }} />
              </Overlay>
            )}

            <CustomizedSnackbars
              variant={this.state.notificationVariant}
              message={this.state.notificationMessage}
              isOpen={this.state.openNotification}
              toClose={this.closeNotification}
            />
          </Container>
        </main>

        <Footer />
      </div>
    );
  }
}

ProviderPredefinedMessageTemplates.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = (state) => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession
});

export default connect(mapStateToProps)(
  withStyles(Styles)(ProviderPredefinedMessageTemplates)
);
